import * as Sentry from "@sentry/nextjs";

export const chance = (oneIn: number): boolean => {
  return Math.floor(Math.random() * oneIn) === 0;
};

/**
 * Sentry event filter that decides which errors to send to Sentry and which to drop.
 *
 * ## Filtering (Always Dropped)
 * These errors are completely filtered out and never sent to Sentry:
 * - **ResizeObserver loop errors** - Browser noise, not actionable
 * - **Network request failed** (TypeError) - ~30% of Sentry quota, usually offline/flaky connection
 * - **Aborted XHR/fetch** (status_code 0) - User navigated away, backend never received request
 * - **AbortError/Aborted** - User cancelled request, not actionable
 *
 * ## Sampling (Randomly Dropped)
 * These errors are sampled to reduce volume while preserving a representative sample:
 * - **4xx Client Errors** - 10% sampled (1 in 10)
 * - **GraphQL 403 Errors** - 5% sampled (1 in 20) - cryptic issues with no status code
 * - **ChunkLoadError/Loading chunk** - 10% sampled - common with lazy loading
 *
 * ## Grouping (Fingerprinted)
 * These errors are grouped by endpoint for easier debugging:
 * - **JSON Parse errors** - Fingerprinted as "json-parse-error:<endpoint>"
 *
 * ## Pass-Through
 * All other errors pass through unchanged:
 * - 5xx Server errors
 * - Unhandled exceptions
 * - GraphQL errors with other status codes
 *
 * @param event - The Sentry event to filter
 * @returns The event to send, or null to drop
 */
export const beforeSend = (event: Sentry.Event): Sentry.Event | null => {
  const exception = event.exception?.values?.[0];

  // Check for 4xx client request errors
  const statusCode = Number(event.tags?.status ?? event.tags?.statusCode ?? 0);
  const isClientRequestError = statusCode >= 400 && statusCode < 500;

  if (isClientRequestError) {
    return chance(10) ? event : null;
  }

  // Reduce sampling even more for cryptic 403 issues. These have no status codes
  // except for text inside the error message and are the highest sent event to Sentry.
  // Example of issue: https://sentry.io/organizations/flicket-ltd/issues/3189004560/?project=6314319
  const isGraphQL_403 =
    event.message?.includes("GraphQL Error (Code: 403)") ||
    exception?.value?.includes("GraphQL Error (Code: 403)");

  if (isGraphQL_403) {
    return chance(20) ? event : null;
  }

  // Add filter for chunk load errors (common with lazy loading)
  if (
    exception?.value?.includes("ChunkLoadError") ||
    exception?.value?.includes("Loading chunk")
  ) {
    return chance(10) ? event : null;
  }

  // Filter ResizeObserver errors (non-actionable browser noise)
  if (exception?.value?.includes("ResizeObserver loop")) {
    return null;
  }

  // Drop network request failed - represents roughly 30% of SEntry quota 🤮
  // Offline / flaky connection
  // Adblock / CORS blocks
  if (
    exception?.type === "TypeError" &&
    exception?.value?.includes("Network request failed")
  ) {
    return null;
  }

  // Drop aborted XHR/fetch (status_code 0)
  // These are almost never actionable — your backend never received them. Logging these just wastes Sentry quota.
  const breadcrumbs = event.breadcrumbs ?? [];

  const hasStatusZero = breadcrumbs.some(
    (b) =>
      b.category === "xhr" &&
      b.data &&
      (b.data as Record<string, unknown>).status_code === 0
  );

  if (hasStatusZero) {
    return null;
  }

  // SyntaxError: JSON Parse error: Unexpected identifier "object"
  // JSON.parse(response) // but response is already an object
  if (exception?.value?.includes("JSON Parse error")) {
    const endpoint = event.request?.url ?? "unknown";
    // This groups them into one issue.
    event.fingerprint = ["json-parse-error", endpoint];
  }

  if (
    exception?.value?.includes("AbortError") ||
    exception?.value?.includes("Aborted")
  ) {
    return null;
  }

  return event;
};
